<template>
	<v-container fill-height fluid>
		<v-dialog v-model="addPinnedAccountDialog" max-width="500" content-class="rounded-lg">
			<v-card>
				<v-card-title>{{$t('Home.pin-account')}}</v-card-title>
				<v-card-text class="pb-0 pt-3">
					<v-row>
						<v-col cols="8">
							<v-autocomplete 
							v-model="accountsId"
							dense
							outlined
							multiple
							:label="$t('inputs.account')"
							:items="configs.accounts.filter(v => !pinnedAccounts.includes(v.id))"
							item-text="name"
							item-value="id"
							>
								<template v-slot:item="data">
									<v-list-item
										:class="type.main === data.item.type ? 'orangeLighten5': 'none'"
										v-on="data.on"
										v-bind="data.attrs"
									>
										<v-list-item-action  class="me-4 ms-0">
											<v-checkbox :value="data.attrs.inputValue" :ripple="false"></v-checkbox>
										</v-list-item-action>
										{{data.item.name}}
										<v-spacer></v-spacer>
										<bee-advanced-money-label
											:with-color="true"
											:value="data?.item?.account_statement_summary"
											:show-zeros-values="true"
										/>
		
									</v-list-item>
								</template>
								<template v-slot:selection="{ item, index }">
									<div v-if="index === 0">
										<span>{{ item.name }}</span>
									</div>
									<span
										v-if="index === 1"
										class="grey--text text--darken-2 text-caption ms-2"
										>
										(+{{  accountsId.length - 1 }})
									</span>
								</template>
							</v-autocomplete>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="">
					<v-btn
						text
						color="greenDarken1"
						:loading="addLoading"
						@click="updatePinnedAccounts(accountsId)"
					>
						{{ $t('buttons.pin') }}
					</v-btn>
					<v-btn
						text
						color="redDarken1"
						@click="addPinnedAccountDialog = false; accountsId = null"
					>
						{{ $t('buttons.cancel') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-row class="justify-space-between">
			<v-col md="8" cols="12">
				<div class="d-flex flex-wrap justify-md-start justify-center align-center">
					<div cols="auto">
						<v-img
							:src="fileLogo"
							width="200px"
							lazy-src="@/assets/placeholder-image.png"
						/>
					</div>
					<div class="d-flex flex-column flex-wrap ms-md-10">
						<!-- company name -->
						<div class="text-h6 mb-2 primary--text">
							{{currentFinancialFile.company_name}}
						</div>

						<!-- cashbox -->
						<div v-if="!isWarehouse" class="mb-3">
							<span class="secondary--text text-body-2">
								{{$t('Home.financial-file-fund')}}:
							</span>
							<span class="primary--text">
								{{box_account_info ? box_account_info : $t('Home.undefined')}}
							</span>
						</div>

						<!-- file state -->
						<div class="mb-3">
							<span class="econdary--text text-body-2">
								{{$t('Home.file-state')}}:
							</span>
							<span class="primary--text">
								{{file_status}}
								<v-icon
									class="ms-1 pb-1"
									color="primary"
									size="20"
								>{{file_is_locked ? 'mdi-lock-outline': 'mdi-lock-open-variant'}}</v-icon>
							</span>
						</div>

						<!-- posted files -->
						<div v-if="!isWarehouse">
							<router-link
								:to="{name: 'journals'}"
								class="secondary--text text-decoration-none"
							>
								<div class="mb-2">
									<span class="text-body-2">{{$t('Home.total-bonds')}}:</span>
									<span class="primary--text mx-2">{{num_of_total_bonds}}</span>
									 
									<span class="text-body-2">({{bond_posting_ratio_tooltip}})</span>
								</div>
								<div class="d-flex align-center">
									<span class="primary--text me-2">
										{{bond_posting_ratio.toFixed(2) +'%'}}
									</span>
									<v-progress-linear
										color="success-color lighten-1"
										rounded
										height="5"
										:value="bond_posting_ratio"
										:style="{ maxWidth: '180px' }"
									></v-progress-linear>
								</div>
							</router-link>
						</div>
					</div>
				</div>
				<div v-if="usePinnedAccounts && !isWarehousePermission" class="mt-2 ">						
					<v-row>
						<v-col cols="11">
							<v-card flat>
								<v-card-title>
									<span class="primary--text">
										<i class="fas fa-thumbtack mx-1"></i>
										{{ $t('Home.pinned-accounts') }}
									</span>
									<v-divider class="mx-4"></v-divider>
									<v-btn 
										color="greenDarken1"
										class="white--text pa-2"
										:loading="addLoading"
										@click="addPinnedAccountDialog = true"
									>
										{{$t('Home.pin-account')}}
										<i class="fas fa-plus mx-1"></i>
									</v-btn>
								</v-card-title>
								<v-card-text>
									<v-virtual-scroll
									:items="grouped_pinned_accounts"
									bench="6"
									item-height="62"
									:height="$vuetify.breakpoint.height - 400"
								  >
									<template v-slot:default="{ item }">
										<v-row class="ma-0">
											<v-col v-for="subItem in item" :key="subItem.id" cols="4" class="py-1 px-1">
												<v-card
													class="rounded-lg"
													link
													@click="redirectToAccountStatement(subItem.id)"
												>
													<v-list-item class="px-3">
														<v-list-item-content class="py-1">
															<v-list-item-title class="d-flex flex-column">
																<span class="font-weight-bold primary--text">
																	{{ subItem.name }}
																</span>
																<bee-advanced-money-label
																	:with-color="true"
																	:value="subItem?.account_statement_summary"
																	:show-zeros-values="true"
																/>
															</v-list-item-title>
														</v-list-item-content>
											  
														<v-list-item-action>
				
															<v-tooltip top>
																<template v-slot:activator="{on,attrs}">
																	<v-btn
																		v-on="on"
																		v-bind="attrs"
																		color="primary"
																		icon
																		small
																		@click.stop="updatePinnedAccounts(subItem.id, false)"
																	>
														
																		<v-icon
																			color="primary"
																		>
																			mdi-pin-off-outline
																		</v-icon>
																	</v-btn>
																</template>
																<span>{{$t('Home.unpin-account')}}</span>
															</v-tooltip>
														</v-list-item-action>
													  </v-list-item>
												</v-card>
											</v-col>
										</v-row>
									</template>
								  </v-virtual-scroll>
								</v-card-text>
							</v-card>
						</v-col>
					</v-row>
				</div>
			</v-col>

			<!-- weather  -->
			<v-col md="4" cols="12">
				<vue-weather
					id="weather"
					:api-key="open_weather_map_api_key"
					:latitude="latitude"
					:longitude="longitude"
					:bar-color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
					:text-color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
					:language="lang"
					units="uk"
					ref="weather"
				>
					<template v-if="location.address" v-slot:title>
						{{ `Weather for ${location.address.country}, ${location.address.city}` }}
					</template>
				</vue-weather>
			</v-col>
		</v-row>

		<v-bottom-navigation
			app
			height="26"
			class="evaluation-0"
		>
			<v-row align="center" justify="center" dir="ltr" style="font-size: 12px">
				<span>Powered by</span>
				<span class="orange--text mx-1">BEETRONIX</span>
				<span>| Emerald {{settings.SYSTEM_VERSION}}</span>
			</v-row>
		</v-bottom-navigation>
	</v-container>
</template>
<script>
import { mapGetters, mapState } from "vuex";
import * as settings from "../../settings/index.js";
// import store from "../../store/index.js";
import i18n from "../../i18n/i18n"
import VueWeather from "vue-weather-widget";
import { type, userType } from "@/helpers/enums";
import { usePinnedAccounts } from '@/configs/global.js'
export default {
	components: {
    	VueWeather,
    },
	data: () => {
		return {
			settings: settings,
			usePinnedAccounts,
			// location and weather
			open_weather_map_api_key: '5796abbde9106b7da4febfae8c44c232', // For more info: https://github.com/dipu-bd/vue-weather-widget
			gettingLocation: false,
			errorStr: null,
			type,
			// location coordinates
			latitude: '36.21423656415479',
			longitude: '37.137632749490834',
			addPinnedAccountDialog: false,
			pinnedAccounts: [],
			accountsId: null,
			addLoading: true
		};
    },
    
	computed: {
		...mapState("configs", ["configs"]),
		...mapState("weather", ["location"]),
		...mapState({
			currentFinancialFile: state => state.financialFiles.current_financial_file,
			userTypePermission: state => state.permissions.user_type,
		}),
		...mapGetters({
			isWarehouse: 'permissions/isWarehouse'
		}),
		num_of_total_bonds() {
			return this.configs?.num_of_total_bonds;
		},
		bond_posting_ratio_tooltip() {
			return this.configs?.num_of_not_posted_bonds
				? ` ${this.$t('Home.not-posted')}: ${this.configs.num_of_not_posted_bonds}`
				: this.$t('Home.all-posted-bonds');
		},
		bond_posting_ratio() {
			return this.num_of_total_bonds
				? 100 - (this.configs.num_of_not_posted_bonds * 100) / this.num_of_total_bonds
				: 100;
		},
		bond_posting_ratio_caption() {
			return `${this.configs?.num_of_not_posted_bonds}/${this.num_of_total_bonds}`;
		},
		file_is_locked() {
			return this.configs?.financial_file?.is_locked;
		},
		file_status() {
			return this.file_is_locked ? this.$t('Home.locked') : this.$t('Home.unlocked');
		},
		box_account() {
			return this.configs?.accounts?.find(c => c.is_box);
		},
		box_account_info() {
			return this.box_account
				? this.box_account.accounting_no + "-" + this.box_account.name
				: "";
		},
		fileLogo() {
			return ( 
				this.currentFinancialFile.company_logo_file
					? (
						axios.defaults.baseURL.replace("/api/", "") +
						"/Content/FinancialFiles/Logos/" +
						this.currentFinancialFile.company_logo_file
					)
					: require('@/assets/Emerald.png')
			);
		},
		lang() { 
			return i18n.locale
		},
		grouped_pinned_accounts() {
			const items = this.configs.accounts.filter(v => this.pinnedAccounts.includes(v.id));
			const result = [];
			for (let i = 0; i < items.length; i += 3) {
				result.push(items.slice(i, i + 3));
			}
			return result;
		},
		items() {
			return [
				{title: this.$t('buttons.account-statement'), action: (id) => this.$router.push({name:'account-statement', params: {id: id}}) },
				{title: this.$t('Home.unpin-account'), action: id => this.updatePinnedAccounts(id, false)}
			]
		},
		isWarehousePermission() {
			return (this.userTypePermission === userType.warehouseKeeper) || (this.userTypePermission === userType.warehouseManger);
		}
	},

	methods: {
		getLocationCoords() {
			this.gettingLocation = true;
			// get position
			navigator.geolocation.getCurrentPosition(pos => {
				this.gettingLocation = false;
				this.latitude = pos?.coords?.latitude?.toString();
				this.longitude = pos?.coords?.longitude?.toString();

				// https://www.geeksforgeeks.org/how-to-get-city-name-by-using-geolocation/
				this.$store.dispatch('weather/getLocation', { lat: this.latitude, lon: this.longitude })
			}, err => {
				this.gettingLocation = false;
				this.errorStr = err.message;
			})
		},
		getPinnedAccounts() {
			this.$store.dispatch('accounts/getPinnedAccounts').then(data => {
				this.pinnedAccounts = data;
				this.addLoading = false;
			})
		},
		updatePinnedAccounts(ids, isAdd = true) {
			this.addLoading = true;
			let updatePinnedAccounts = [];
			if(isAdd) {
				updatePinnedAccounts = [...this.pinnedAccounts, ...ids];
			} else {
				updatePinnedAccounts = this.pinnedAccounts.filter(v => v !== ids);
			}
			this.$store.dispatch('accounts/addPinnedAccounts', updatePinnedAccounts).then(() => {
				this.getPinnedAccounts();
				this.addPinnedAccountDialog = false;
				this.accountsId = null;
				this.$store.dispatch('configs/fetchAll')
			})
		},
		redirectToAccountStatement(id) {
			const route = this.$router.resolve({
				name: 'statement',
				params: { id: id }
			})
            window.open(route.href, '_blank');
		}
	},

	created() {
		if (!Object.keys(this.configs).length) this.$router.go(0);
		// get location for weather
		this.getPinnedAccounts();
		
		if('geolocation' in navigator) {
			navigator.permissions.query({ name: 'geolocation' }).then(res => {
				res.onchange = () => {
					if (res.state === 'granted') {
						this.getLocationCoords();
					}
				};
				
				if (res.state === 'granted') {
					this.getLocationCoords();
				} else {
					this.$store.dispatch('weather/getLocation', { lat: this.latitude, lon: this.longitude })
				}
			})
			
			return;
		} else {
			this.errorStr = 'Geolocation is not available.';
		}
	},
	/*
	async beforeRouteEnter(to, from, next) {
        let promises = [];
     
		let object = store.state.configs.configs;
		if (Object.keys(object).length == 0)
			promises.push(store.dispatch("configs/fetchAll"));

		if (promises.length) await Promise.all(promises);

		next();
	}
	*/
};
</script>
<style lang="scss">
#weather {
	.vww {
		&__header {
			text-align: start;
		}

		@media screen and (min-width: 960px) {
			&__content {
				flex-direction: column;
				height: 450px;
			}
			&__daily {
				width: 100%;
			}
		}
	}
}
.container.fill-height {
	align-items: unset;
	display: flex;
	flex-wrap: wrap;
}
</style>
